import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useRef, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const BOM = () => {
    const privileges = secureLocalStorage.getItem("privileges");
    const userID = secureLocalStorage.getItem("userID");
    const navigate = useNavigate();
    const { apiClient } = useGlobalAPIQueryContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialQueries = searchParams.get("q")?.split(",") || [];
    const initialColumns = searchParams.get("c")?.split(",") || [];

    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");
    const [cleanBomLoading, setCleanBomLoading] = useState(false);
    const cleanBomFileRef = useRef(null);
    const cleanBomUploadModeRef = useRef("validate");

    const { useFetch } = useAPIQuery({
        key: "bom",
        endpoint: "/api/bom/?action=BOM",
    });

    const { data, isLoading, isFetching } = useFetch({
        params: {
            entries: entries,
            page_no: pageIndex + 1,
            q: globalFilterValue !== "" ? globalFilterValue : columnFilterValue,
            c: globalFilterValue === "" ? columnFilterKey : "",
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const { useDelete } = useAPIQuery({
        key: "bom",
        endpoint: "/api/bom/",
    });

    const columns = [
        {
            accessor: "product",
            header: "Product Name",
            cell: (row) => (
                <>
                    {privileges.includes("bom_details") ? (
                        <p
                            style={{ cursor: "pointer" }}
                            className="link"
                            onClick={() =>
                                navigate("/bom-details", {
                                    state: {
                                        item: row,
                                    },
                                })
                            }
                        >
                            {row?.product?.item_name}
                        </p>
                    ) : (
                        <p>{row?.product?.item_name}</p>
                    )}
                </>
            ),
        },
        {
            accessor: "final_good",
            header: "Final Good",
            cell: (row) => <p>{row?.final_good?.item_name}</p>,
        },
        {
            accessor: "variant",
            header: "Variant",
        },
        {
            accessor: "no_shades",
            header: "No of Shades",
        },
    ];

    const { mutate: deleteData } = useDelete();

    const downloadCleanBomFile = async (action) => {
        try {
            setCleanBomLoading(true);
            const response = await apiClient.get("/api/boms/bulk/", {
                params: { action },
                responseType: "blob",
            });
            const blob = response.data;
            const contentDisposition = response.headers["content-disposition"];
            const filename =
                contentDisposition
                    ?.split("filename=")?.[1]
                    ?.replaceAll('"', "") ||
                (action === "clean-template"
                    ? "clean_bom_template.csv"
                    : action === "details-export"
                    ? "bom_with_details.xlsx"
                    : "clean_bom_export.csv");

            const href = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = href;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(href);
        } catch (error) {
            Swal.fire({
                title: "BOM Download Failed",
                text: error.message,
                icon: "error",
            });
        } finally {
            setCleanBomLoading(false);
        }
    };

    const openCleanBomUpload = (mode) => {
        cleanBomUploadModeRef.current = mode;
        cleanBomFileRef.current?.click();
    };

    const handleCleanBomFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }

        const mode = cleanBomUploadModeRef.current;
        const shouldApply = mode === "apply";

        if (shouldApply) {
            const result = await Swal.fire({
                text: "This will replace active BOM rows for the final goods present in the CSV.",
                showDenyButton: true,
                confirmButtonText: "Import",
                denyButtonText: "Cancel",
            });
            if (!result.isConfirmed) {
                return;
            }
        }

        try {
            setCleanBomLoading(true);
            const formData = new FormData();
            formData.append("file", file);
            formData.append("created_by", userID);
            formData.append("updated_by", userID);

            const response = await apiClient.post("/api/boms/bulk/", formData, {
                params: {
                    action: "clean-import",
                    dry_run: shouldApply ? "false" : "true",
                    replace: "true",
                    replace_scope: "uploaded_final_goods",
                },
            });
            const result = response.data;

            Swal.fire({
                title: shouldApply ? "BOM Imported" : "BOM CSV Valid",
                text: shouldApply
                    ? `${result.imported_count} BOM rows imported.`
                    : `${result.valid_row_count} BOM rows are valid.`,
                icon: "success",
            });
        } catch (error) {
            const firstErrors = error.response?.data?.errors
                ?.slice(0, 5)
                ?.map((error) => `Row ${error.row}: ${error.error}`)
                ?.join("\n");
            Swal.fire({
                title: "BOM Import Failed",
                text:
                    firstErrors ||
                    error.response?.data?.detail ||
                    error.response?.data?.error ||
                    error.message,
                icon: "error",
            });
        } finally {
            setCleanBomLoading(false);
        }
    };

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        if (q !== "") {
            setGlobalFilterValue(q);
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.delete("c");
                return searchParams;
            });
        } else if (q === "") {
            setGlobalFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.delete("q");
                return searchParams;
            });
        }
    };

    const handleColumnFilterChange = (value) => {
        const q = value.map((val) => val.value?.trim() || "").join(",");
        const c = value.map((val) => val.id).join(",");
        if (q !== "" && c !== "") {
            setGlobalFilterValue("");
            setColumnFilterKey(c);
            setColumnFilterValue(q);
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.set("c", c);
                return searchParams;
            });
        } else if (q === "" && c === "") {
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.delete("q");
                searchParams.delete("c");
                return searchParams;
            });
        }
    };

    const handlePagination = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set("entries", value.pageSize);
            searchParams.set("page", value.pageIndex + 1);
            return searchParams;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleEditRow = (row) => {
        console.log(row);
        navigate("/edit-bom", { state: { editpost: row.original } });
    };

    const handleDeleteRow = (row) => {
        console.log(row);
        const entry_id = row.original.id;
        Swal.fire({
            text: "Are you sure? You will not be able to recover this data!",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
        }).then((result) => {
            if (result.isConfirmed) {
                deleteData({ id: entry_id });
            }
        });
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">BOM</h3>
                    </div>
                    <div className="nk-block-head-content d-flex flex-wrap align-items-center">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2 mb-1"
                            onClick={() => downloadCleanBomFile("clean-template")}
                            disabled={cleanBomLoading}
                        >
                            <em className="icon ni ni-download me-1"></em>
                            <span>Template</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2 mb-1"
                            onClick={() => downloadCleanBomFile("clean-export")}
                            disabled={cleanBomLoading}
                        >
                            <em className="icon ni ni-download me-1"></em>
                            <span>Clean Export</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2 mb-1"
                            onClick={() => downloadCleanBomFile("details-export")}
                            disabled={cleanBomLoading}
                        >
                            <em className="icon ni ni-download me-1"></em>
                            <span>BOM Details Export</span>
                        </button>
                        {privileges.includes("bom_create") && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary me-2 mb-1"
                                    onClick={() => openCleanBomUpload("validate")}
                                    disabled={cleanBomLoading}
                                >
                                    <em className="icon ni ni-check me-1"></em>
                                    <span>Validate CSV</span>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger me-2 mb-1"
                                    onClick={() => openCleanBomUpload("apply")}
                                    disabled={cleanBomLoading}
                                >
                                    <em className="icon ni ni-upload me-1"></em>
                                    <span>Import Fresh</span>
                                </button>
                                <input
                                    ref={cleanBomFileRef}
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="d-none"
                                    onChange={handleCleanBomFileChange}
                                />
                            </>
                        )}
                        {privileges.includes("bom_create") && (
                            <>
                            <NavLink
                                to="/create-bom"
                                className="toggle btn btn-icon btn-primary d-md-none"
                                id="button-button-create-client"
                            >
                                <em className="icon ni ni-plus"></em>
                            </NavLink>

                            <NavLink
                                to="/create-bom"
                                className="toggle btn btn-primary d-none d-md-inline-flex"
                                id="button-button-create-client"
                            >
                                <em className="icon ni ni-plus"></em>
                                <span>Create BOM</span>
                            </NavLink>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Table
                data={data ? data?.results.data : []}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={data ? data.count : 0}
                globalSearch
                isLoading={isLoading || isFetching}
                editRows={privileges.includes("bom_update") ? true : false}
                deleteRows={privileges.includes("bom_delete") ? true : false}
                defaultGlobalSearchValue={
                    initialQueries.length > 0 && initialColumns.length <= 0
                        ? initialQueries[0]
                        : undefined
                }
                defaultColumnFiltersValue={
                    initialQueries.length > 0 && initialColumns.length > 0
                        ? initialColumns.map((column, index) => ({
                              id: column,
                              value: initialQueries?.[index] ?? "",
                          }))
                        : undefined
                }
                filterModal={false}
                selectRows={false}
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFilterChange={handleColumnFilterChange}
                onRowEdit={handleEditRow}
                onRowDelete={handleDeleteRow}
                onPaginationChange={handlePagination}
            />
        </>
    );
};

export default BOM;
